import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getFirstAllowedPath, getRequiredPrivilegeForPath } from '../types/assistantPrivileges';

interface PrivilegeGuardProps {
  children: ReactNode;
}

const PrivilegeGuard = ({ children }: PrivilegeGuardProps) => {
  const location = useLocation();
  const { isAssistant, hasPrivilege } = useUser();

  if (!isAssistant()) {
    return <>{children}</>;
  }

  // Block assistants from accessing these routes regardless of privileges
  const blockedPaths = ['/system-users', '/teachers', '/subjects'];
  if (blockedPaths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'))) {
    return <Navigate to={getFirstAllowedPath(hasPrivilege)} replace />;
  }

  const required = getRequiredPrivilegeForPath(location.pathname);
  if (!required || hasPrivilege(required)) {
    return <>{children}</>;
  }

  const fallback = getFirstAllowedPath(hasPrivilege);
  if (fallback === location.pathname) {
    return <>{children}</>;
  }

  return <Navigate to={fallback} replace />;
};

export default PrivilegeGuard;
